import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import FAQ from '@/components/base/FAQ';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/site';

function ContactForm() {
  return (
    <form
      data-readdy-form="true"
      action="https://readdy.ai/api/form/d9sm53h4ne17ei0sdju0"
      method="POST"
      className="space-y-4"
    >
      <div>
        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          您的姓名 <span className="text-primary-500">*</span>
        </label>
        <input
          type="text"
          id="about-name"
          name="name"
          required
          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          电子邮箱 <span className="text-primary-500">*</span>
        </label>
        <input
          type="email"
          id="about-email"
          name="email"
          required
          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="about-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
      </div>
      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="spam-field"
      />
      <button
        type="submit"
        className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-send-plane-line mr-2"></i>发送留言
      </button>
    </form>
  );
}

export default function AboutPage() {
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、发展历程与联系方式`,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      foundingDate: siteInfo.foundingYear,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、核心优势与联系方式。${siteInfo.description}`}
        keywords={`关于蘑菇TV入口,${siteInfo.name}品牌,联系方式`}
        canonical={`${siteInfo.url}/about`}
        ogTitle={`关于我们 - ${siteInfo.name}`}
        ogDescription={`了解${siteInfo.name}的品牌故事、核心优势与联系方式`}
      />
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14">
        {/* Page Header */}
        <section className="px-4 md:px-6 bg-background-100 py-14">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading">关于我们</h1>
            <p className="text-foreground-500 mt-2">了解蘑菇TV入口的品牌故事与使命</p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-6 font-heading">品牌故事</h2>
            <div className="text-foreground-600 leading-relaxed space-y-4 text-sm">
              {siteInfo.brandStory.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph.trim()}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Features / Core Advantages */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 text-center mb-10 font-heading">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {siteInfo.features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading whitespace-nowrap">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-primary-500">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {siteInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1 font-heading">{stat.value}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl font-bold text-foreground-900 mb-6 font-heading">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <a href={`mailto:${siteInfo.contactInfo.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
                        {siteInfo.contactInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                      <p className="text-sm text-foreground-500">{siteInfo.contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                      <i className="ri-weibo-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">官方微博</p>
                      <p className="text-sm text-foreground-500">{siteInfo.contactInfo.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">工作时间</p>
                      <p className="text-sm text-foreground-500">{siteInfo.contactInfo.workingHours}</p>
                      <p className="text-xs text-foreground-400 mt-1">{siteInfo.contactInfo.responseTime}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground-900 mb-6 font-heading">给我们留言</h2>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <FAQ
              items={siteInfo.faqs}
              title="常见问题"
              subtitle="以下是用户最常咨询的问题"
            />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}